'use client'

import { IconButton, Menu, Portal } from '@chakra-ui/react'
import React, { ReactNode } from 'react'
import { LuEllipsis } from 'react-icons/lu'

export type ThreeDotsMenuItem =
  | {
      key: string
      component: ReactNode
      destructive?: boolean
    }
  | {
      key: string
      label: ReactNode
      icon?: ReactNode
      destructive?: boolean
      disabled?: boolean
    }

export const ThreeDotsMenu = ({
  ariaLabel,
  buttons,
  onSelect,
  /** e.g. material card vignette: subtle fill so the trigger stays visible on light photos */
  subtleTriggerBackground = false,
}: {
  ariaLabel: string
  buttons: ThreeDotsMenuItem[]
  /** When provided, menu closes on select and this is called with the item key. Use with label+icon items. */
  onSelect?: (key: string) => void
  subtleTriggerBackground?: boolean
}) => {
  return (
    <Menu.Root
      positioning={{ placement: 'bottom-end' }}
      onSelect={
        onSelect ? (e: { value: string }) => onSelect(e.value) : undefined
      }
    >
      <Menu.Trigger asChild>
        <IconButton
          aria-label={ariaLabel}
          variant="ghost"
          size="2xs"
          {...(subtleTriggerBackground
            ? {
                bg: 'bg.subtle',
                _hover: { bg: 'bg.muted' },
                _expanded: { bg: 'bg.muted' },
              }
            : {})}
        >
          <LuEllipsis />
        </IconButton>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content>
            {buttons
              .filter(
                (item): item is ThreeDotsMenuItem =>
                  item != null &&
                  typeof item === 'object' &&
                  'key' in item &&
                  (('label' in item && item.label != null) ||
                    ('component' in item && item.component != null))
              )
              .map((item) => {
                const key = item.key
                const destructive = item.destructive
                const isLabel = 'label' in item && item.label != null
                const disabled = isLabel
                  ? (item as { disabled?: boolean }).disabled
                  : undefined
                return (
                  <Menu.Item
                    key={key}
                    value={key}
                    disabled={disabled}
                    {...(destructive && { color: 'fg.error' })}
                  >
                    {isLabel ? (
                      <>
                        {item.icon}
                        {item.icon != null && ' '}
                        {item.label}
                      </>
                    ) : (
                      (item as { component: ReactNode }).component
                    )}
                  </Menu.Item>
                )
              })}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
}
